import { Button } from '@trackman/web-shared-components';
import './OrderSummary.scss';
import Subtitle from 'components/TextComps/Subtitle';
import { useCartStore } from 'stores/SubscriptionsAndPricing/CartStore';
import { CartItem } from 'types/cart';
import { Trans, useTranslation } from 'react-i18next';
import { ReactComponent as Trash } from 'images/icons/Trash.svg';
import { useQuery } from '@tanstack/react-query';
import { useServices } from 'index';
import { useCallback, useMemo, useState } from 'react';
import { removeCartItemFromLocalStorage } from 'utilities/cartLocalStorageHelper';
import { DefaultRoutes } from 'Routes';
import { useNavigate } from 'react-router-dom';
import CheckboxWithoutLabel from '@trackman/web-shared-components/components/Checkbox/CheckboxWithoutLabel';
import { ReactComponent as ShoppingCart } from 'images/icons/ShoppingCart.svg';

export const OrderSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const profileService = useServices().profileService;
  const { data: isPremium, isPending } = useQuery({
    queryKey: ['isUserMember'],
    queryFn: profileService.checkIfUserIsMember,
    refetchOnWindowFocus: false,
  });

  const [agreedToTermsAndConditions, setAgreedToTermsAndConditions] = useState(false);

  const removeItemFromCart = useCartStore((state) => state.removeItemFromCart);
  const itemsInCart: CartItem[] = useCartStore((state) => state.items);

  const totalPrice = useMemo(() => {
    return itemsInCart.reduce((total, item) => {
      if ((itemsInCart.some((cartItem) => cartItem.id === 'premium') || isPremium) && item.discountedPrice) {
        return total + item.discountedPrice;
      }
      return total + item.basePrice;
    }, 0);
  }, [itemsInCart, isPremium]);
  const certificationsInCart = useMemo(() => itemsInCart.filter((item) => item.id !== 'premium'), [itemsInCart]);
  const hasDiscount = useMemo(() => {
    return (!isPending && isPremium) || itemsInCart.some((item) => item.id === 'premium');
  }, [isPremium, isPending, itemsInCart]);

  const handleRemovingEntryFromCart = useCallback(
    (itemId: string) => {
      removeItemFromCart(itemId);
      removeCartItemFromLocalStorage(itemId);
    },
    [removeItemFromCart]
  );

  return (
    <div className='order-summary'>
      <div className='order-summary-card'>
        {itemsInCart.length > 0 ? (
          <>
            <Subtitle>
              <>{t('OrderSummary', { ns: 'subscriptionsAndPricing' })}</>
            </Subtitle>
            <div className='items-list'>
              {itemsInCart.find((item) => item.id === 'premium') && (
                <SubscriptionSummaryEntry
                  key='premium'
                  subscription={itemsInCart.find((item) => item.id === 'premium')!}
                  removeItemFromCart={handleRemovingEntryFromCart}
                />
              )}
              {certificationsInCart.map((item) => (
                <CertificationSummaryEntry
                  key={item.id}
                  certification={item}
                  hasDiscount={hasDiscount}
                  removeItemFromCart={handleRemovingEntryFromCart}
                />
              ))}
            </div>
            <div className='summary-totals'>
              <div className='summary-row'>
                <span className='title'>{t('VAT', { ns: 'subscriptionsAndPricing' })}</span>
                <span className='price'>$0.00</span>
              </div>
              <div className='summary-row total-row'>
                <span className='title'>{t('TodaysTotal', { ns: 'subscriptionsAndPricing' })}</span>
                <span className='price'>${totalPrice}</span>
              </div>
            </div>
            <div className='purchase-disclaimer'>
              <span>{t('PurchaseDisclaimer', { ns: 'subscriptionsAndPricing' })}</span>
              <div>
                <CheckboxWithoutLabel onChange={(e) => setAgreedToTermsAndConditions(e.target.checked)} />
                <Trans i18nKey='TermsAndConditions' ns='subscriptionsAndPricing'>
                  I agree to the&nbsp;
                  <span onClick={() => navigate(DefaultRoutes.TermsAndConditions)} className='terms-and-conditions-link'>
                    Terms and conditions
                  </span>
                  &nbsp;.
                </Trans>
              </div>
            </div>
          </>
        ) : (
          <span className='empty-cart'>
            <ShoppingCart /> {t('YourCartIsEmpty', { ns: 'subscriptionsAndPricing' })}
          </span>
        )}
      </div>
      {itemsInCart.length > 0 ? (
        <Button className='cart-action-button' intent='primary' disabled={!agreedToTermsAndConditions}>
          {t('ContinueToPayment', { ns: 'subscriptionsAndPricing' })}
        </Button>
      ) : (
        <Button className='cart-action-button' intent='primary' onClick={() => navigate(DefaultRoutes.SubscriptionsAndPricing)}>
          {t('ViewWebshop', { ns: 'subscriptionsAndPricing' })}
        </Button>
      )}
    </div>
  );
};

const SubscriptionSummaryEntry = ({
  subscription,
  removeItemFromCart,
}: {
  subscription: CartItem;
  removeItemFromCart: (id: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className='summary-row'>
      <Trash onClick={() => removeItemFromCart(subscription.id)} />
      <div className='title title__subscription'>
        <span>{subscription.title}</span>
        <span>/ {t('Annually', { ns: 'subscriptionsAndPricing' })}</span>
      </div>
      <span className='price'>${subscription.basePrice}</span>
    </div>
  );
};

const CertificationSummaryEntry = ({
  certification,
  hasDiscount,
  removeItemFromCart,
}: {
  certification: CartItem;
  hasDiscount: boolean;
  removeItemFromCart: (id: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className='summary-row'>
      <Trash onClick={() => removeItemFromCart(certification.id)} />
      <span className='title title__certification'>
        {t('Certification', { ns: 'subscriptionsAndPricing', certificationName: certification.title })}
      </span>
      <span className='price'>
        <span className={hasDiscount ? 'base-price base-price_discounted' : 'base-price'}>${certification.basePrice}</span>
        {hasDiscount && <span className='final-price'>${certification.discountedPrice}</span>}
      </span>
    </div>
  );
};
